"use client";

// Italian footer for /it pages (IT-1). A slim copy of
// components/footer/footer.comp.tsx: same styles and layout, Italian labels,
// and links that go to an Italian page when one exists (itHref) or to the
// English page, marked EN, when it does not.
import { FaFacebookF } from "@react-icons/all-files/fa/FaFacebookF";
import { FaInstagram } from "@react-icons/all-files/fa/FaInstagram";
import { FaYoutube } from "@react-icons/all-files/fa/FaYoutube";
import { GrLinkedinOption } from "@react-icons/all-files/gr/GrLinkedinOption";
import { IoIosArrowForward } from "@react-icons/all-files/io/IoIosArrowForward";
import Link from "next/link";
import Image from "next/image";
import cn from "classnames";
import { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { StyledFooter } from "../footer/footer.styles";
import { CSSVars } from "@/styles/styles.config";
import { StyledDiv as StyledBigBanner } from "../bigBanner/bigBanner.styles";
import { usePathname } from "next/navigation";
import { AccordionColumn } from "../footer/accordion-column";
import { subscribeToNewsletter } from "@/helpers/newsletter";
import platforms from "../../../platforms.json";
import { IPlatform } from "../../types";
import { itHref, hasItTwin } from "@/i18n/it/routes";
import { EnTag } from "./langSwitch.comp";

const COMPANY = [
  { name: "Chi siamo", url: "/about-us" },
  { name: "Funzionalità della piattaforma", url: "/features" },
  { name: "Contatti", url: "/contact-us" },
  { name: "Blog", url: "/blog" },
  { name: "Strumenti di marketing gratuiti", url: "/free-tools" },
  { name: "Centro assistenza", url: "https://help.commoninja.com/hc/en-us", external: true },
];

const SOCIAL = [
  { href: "https://www.linkedin.com/company/common-ninja", title: "LinkedIn", icon: <GrLinkedinOption className="icon" /> },
  { href: "https://www.youtube.com/@commonninja", title: "YouTube", icon: <FaYoutube className="icon" /> },
  { href: "https://www.instagram.com/commonninja_official/", title: "Instagram", icon: <FaInstagram className="icon" /> },
  { href: "https://www.facebook.com/CommonNinja/", title: "Facebook", icon: <FaFacebookF className="icon" /> },
];

// The English footer has 6 columns on wide screens; this one has 4, so they
// share the full width instead of sitting in the first 4 of 6 narrow slots
// (long translated names ran into the next one).
const StyledFooterIt = styled(StyledFooter)`
  @media screen and (min-width: ${CSSVars.breakpointXL}) {
    .top {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`;

// The big banner the English footer shows on some pages (bigBannerData in
// components/footer/footer.comp.tsx), for the Italian pages that have one.
// Same markup as components/bigBanner, plus the EN tag when the button leads
// to an English page.
const IT_BIG_BANNER: Record<
  string,
  { title: string; subtitle: string; buttonText: string; buttonLink: string; en?: boolean }
> = {
  "/it/pricing": {
    title: "Offriamo anche piani per le aziende.",
    subtitle: "SE NESSUNO DEI NOSTRI PIANI PREMIUM TI BASTA, NESSUN PROBLEMA!",
    buttonText: "Contattaci",
    buttonLink: "https://commoninja.site/custom-plan",
    en: true,
  },
};

const EnLink = ({ href, children, external }: { href: string; children: React.ReactNode; external?: boolean }) => {
  const target = itHref(href);
  const isEn = !hasItTwin(href);
  return (
    <Link
      href={target}
      hrefLang={isEn ? "en" : "it"}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
    >
      {children}
      {isEn && <EnTag />}
    </Link>
  );
};

// `widgetNames` (slug -> Italian name) is passed in by the server layout so the
// full Italian page copy never ships in the browser bundle.
// `homepageLayout` matches the English footer's prop: on the home page the
// footer sits inside the closing banner's dark background.
export const FooterIt = ({
  widgetNames,
  homepageLayout = false,
}: {
  widgetNames: Record<string, string>;
  homepageLayout?: boolean;
}) => {
  const currentYear = new Date().getFullYear();
  const bigBanner = IT_BIG_BANNER[usePathname() || ""];
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  async function subscribe(e: React.FormEvent) {
    e.preventDefault();
    if (!email) {
      toast.info("Inserisci il tuo indirizzo email.");
      return;
    }
    if (isSubscribing) return;
    setIsSubscribing(true);
    try {
      await subscribeToNewsletter(email);
      setEmail("");
      toast.success("Fatto! Iscrizione completata 🎉");
    } catch {
      toast.error("Si è verificato un errore. Riprova.");
    } finally {
      setIsSubscribing(false);
    }
  }

  return (
    <StyledFooterIt
      className={cn({
        dark: true,
        "homepage-layout": homepageLayout,
        "big-banner": !!bigBanner,
      })}
      lang="it"
    >
      {bigBanner && (
        <StyledBigBanner>
          <h4 className="subtitle">{bigBanner.subtitle}</h4>
          <div className="banner">
            <h4>{bigBanner.subtitle}</h4>
            <h2>{bigBanner.title}</h2>
            <Link
              className="cta"
              href={bigBanner.buttonLink}
              hrefLang={bigBanner.en ? "en" : "it"}
            >
              {bigBanner.buttonText}
            </Link>
            {bigBanner.en && (
              <p style={{ fontSize: 12, opacity: 0.7, marginTop: 10, width: "100%", textAlign: "center" }}>
                Modulo in inglese
              </p>
            )}
          </div>
        </StyledBigBanner>
      )}
      <div className="content-wrapper">
        <div className="top">
          <div className="column">
            <Image src={"/assets/cn-logo-dark.svg"} alt="Common Ninja Logo" width={200} height={30} />
            <div className="subscription-container">
              <p>Novità (in inglese)</p>
              <form className="input-wrapper" onSubmit={subscribe}>
                <div className="input-container">
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="La tua email"
                    type="email"
                    id="footer-email-input"
                  />
                  <button role="button" aria-label="Iscriviti alle novità" disabled={isSubscribing}>
                    <IoIosArrowForward size={16} />
                  </button>
                </div>
              </form>
            </div>
            <h2>Seguici</h2>
            <div className="social-links">
              {SOCIAL.map((s) => (
                <Link
                  key={s.title}
                  rel="noopener noreferrer"
                  href={s.href}
                  target="_blank"
                  aria-label={`Seguici su ${s.title}`}
                  title={s.title}
                >
                  {s.icon}
                </Link>
              ))}
            </div>
          </div>
          <AccordionColumn
            label="Widget in italiano"
            links={
              <>
                {Object.entries(widgetNames).map(([slug, name]) => (
                  <Link key={slug} href={itHref(`/widgets/${slug}`)}>
                    {name}
                  </Link>
                ))}
                <Link className="bold-link" href={itHref("/widgets")}>
                  Vedi tutti i widget
                  {!hasItTwin("/widgets") && <EnTag />}
                  <IoIosArrowForward className="icon" />
                </Link>
              </>
            }
          />
          <AccordionColumn
            label="Piattaforme"
            links={
              <>
                {(platforms as IPlatform[]).slice(0, 7).map((p) => (
                  <EnLink key={p.slug} href={`/platforms/${p.slug}`}>
                    {p.title}
                  </EnLink>
                ))}
                <Link className="bold-link" href={"/platforms"} hrefLang="en">
                  Vedi tutte le piattaforme <EnTag />
                  <IoIosArrowForward className="icon" />
                </Link>
              </>
            }
          />
          <AccordionColumn
            label="Azienda"
            links={
              <>
                {COMPANY.map((l) => (
                  <EnLink key={l.url} href={l.url} external={l.external}>
                    {l.name}
                  </EnLink>
                ))}
              </>
            }
          />
        </div>
        <div className="bottom">
          <small>© Copyright 2012-{currentYear}, Common Ninja. Tutti i diritti riservati.</small>
          <div className="bottom-links">
            <Link href={"/terms"} hrefLang="en">Termini e condizioni <EnTag /></Link>
            <Link href={"/privacy"} hrefLang="en">Informativa sulla privacy <EnTag /></Link>
          </div>
        </div>
      </div>
    </StyledFooterIt>
  );
};
